import { useState } from 'react';

import {
  RiMailLine,
  RiThumbDownFill,
  RiThumbDownLine,
  RiThumbUpFill,
  RiThumbUpLine,
} from '@remixicon/react';

import { useAnalytics } from '@backstage/core-plugin-api';

import { ButtonIcon } from '@spotify-internal/encore-web';
import { ChatEventFragment } from '@spotify/confidence-graphql';

import { useChatContext } from '../ChatThread/ChatContext';
import { type Sentiment, buildFeedbackMailto } from './buildFeedbackMailto';

const findPrecedingUserMessage = (
  events: ChatEventFragment[],
  eventName: string,
): string | undefined => {
  const eventIndex = events.findIndex(e => e.name === eventName);
  if (eventIndex === -1) return undefined;

  for (let i = eventIndex - 1; i >= 0; i--) {
    const msg = events[i].userMessage;
    if (msg) {
      const textFromParts = (msg.parts ?? [])
        .map(p => p.text)
        .filter((t): t is string => !!t)
        .join('\n\n');
      return textFromParts || msg.content || undefined;
    }
  }
  return undefined;
};

const openMailto = (url: string) => {
  window.location.href = url;
};

interface FeedbackToolbarProps {
  eventName: string;
  content: string;
}

export const FeedbackToolbar = ({
  eventName,
  content,
}: FeedbackToolbarProps) => {
  const chatContext = useChatContext();
  const analytics = useAnalytics();
  const [selectedSentiment, setSelectedSentiment] = useState<
    'positive' | 'negative' | null
  >(null);

  const trackFeedback = (sentiment: Sentiment) => {
    analytics.captureEvent('chat-feedback', chatContext?.chatName ?? '', {
      attributes: { sentiment, eventName },
    });
  };

  const handleThumbsFeedback = (sentiment: 'positive' | 'negative') => {
    const next = selectedSentiment === sentiment ? null : sentiment;
    setSelectedSentiment(next);
    if (next) {
      trackFeedback(sentiment);
    }
  };

  const handleComment = () => {
    trackFeedback('comment');

    const userMessage = chatContext
      ? findPrecedingUserMessage(chatContext.chatEvents, eventName)
      : undefined;

    const url = buildFeedbackMailto({
      sentiment: 'comment',
      chatName: chatContext?.chatName ?? '',
      eventName,
      assistantMessage: content,
      userMessage,
      chatUrl: window.location.href,
    });

    openMailto(url);
  };

  return (
    <>
      <ButtonIcon
        variant="tertiary"
        size="small"
        onClick={() => handleThumbsFeedback('positive')}
        aria-label="Positive feedback"
      >
        {selectedSentiment === 'positive' ? (
          <RiThumbUpFill size={16} />
        ) : (
          <RiThumbUpLine size={16} />
        )}
      </ButtonIcon>
      <ButtonIcon
        variant="tertiary"
        size="small"
        onClick={() => handleThumbsFeedback('negative')}
        aria-label="Negative feedback"
      >
        {selectedSentiment === 'negative' ? (
          <RiThumbDownFill size={16} />
        ) : (
          <RiThumbDownLine size={16} />
        )}
      </ButtonIcon>
      <ButtonIcon
        variant="tertiary"
        size="small"
        onClick={handleComment}
        aria-label="Leave a comment"
      >
        <RiMailLine size={16} />
      </ButtonIcon>
    </>
  );
};
